import { Edit05 } from "@untitledui/icons";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { SampleRequestGroup } from "@/api/sample-requests";
import { useSetSampleAllocations } from "@/api/sample-requests";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { error, success } from "@/providers/ToastProvider";
import { MAX_MONTHLY_CAP } from "./allocations-csv";

const HeaderStat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex min-w-[72px] flex-col">
    <span className="text-sm font-medium text-primary">{value}</span>
    <span className="text-xs text-tertiary">{label}</span>
  </div>
);

export function ProductGroupRow({ group }: { group: SampleRequestGroup }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(0);
  const { mutateAsync: setAllocations, isPending } = useSetSampleAllocations();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(group.totalAllocations ?? 0);
    setEditing(true);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
  };

  const handleSave = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    try {
      await setAllocations([{ productId: group.groupId, monthlyCap: draft }]);
      success("Total allocations updated successfully.");
      setEditing(false);
    } catch {
      error("Failed to update total allocations for this product.");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value);
    setDraft(Number.isNaN(next) ? 0 : Math.min(MAX_MONTHLY_CAP, Math.max(0, Math.trunc(next))));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") void handleSave(e);
    if (e.key === "Escape") setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="number"
          min={0}
          max={MAX_MONTHLY_CAP}
          value={String(draft)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          className="w-20 rounded-lg border border-secondary bg-primary px-3 py-2 text-center text-sm text-primary outline-none focus:border-brand"
        />
        <span className="text-xs text-tertiary">Total Allocations</span>
        <Button color="secondary" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button color="primary" size="sm" isLoading={isPending} onClick={(e: React.MouseEvent) => void handleSave(e)}>
          Save
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-6">
      <HeaderStat label="Available" value={group.available ?? "—"} />
      <HeaderStat label="Total Allocations" value={group.totalAllocations ?? "—"} />
      <ButtonUtility size="xs" color="tertiary" tooltip="Edit Allocations" icon={Edit05} onClick={startEditing} />
    </div>
  );
}
